import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DialogFooter } from "@/components/ui/dialog";
import {
  indexFundFormSchema,
  IndexFundFormValues,
  INDEX_FUND_CATEGORIES,
  SUPPORTED_CURRENCIES,
} from "./indexFundForm.schema";
import {IndexFundHolding} from "@/integrations/supabase/indexfunds.models.ts";

interface IndexFundFormProps {
  holding?: IndexFundHolding;
  onSave: (data: IndexFundFormValues) => void | Promise<void>;
  onCancel: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  large_cap_equity: "Large Cap Equity",
  mid_cap_equity: "Mid Cap Equity",
  small_cap_equity: "Small Cap Equity",
  emerging_markets: "Emerging Markets",
  developed_markets: "Developed Markets",
  global_equity: "Global Equity",
  fixed_income: "Fixed Income",
  real_estate: "Real Estate",
  commodity: "Commodity",
  multi_asset: "Multi-Asset",
};

export function IndexFundForm({ holding, onSave, onCancel }: IndexFundFormProps) {
  const form = useForm<IndexFundFormValues>({
    resolver: zodResolver(indexFundFormSchema),
    defaultValues: {
      fundSymbol: holding?.fund_symbol ?? "",
      fundName: holding?.fund_name ?? "",
      fundCategory: holding?.fund_category as IndexFundCategory ?? undefined,
      benchmarkIndex: holding?.benchmark_index ?? "",
      unitsHeld: holding?.units_held ?? undefined,
      averageCostPerUnit: holding?.average_cost_per_unit ?? undefined,
      currency: holding?.currency as typeof SUPPORTED_CURRENCIES[number] ?? undefined,
      purchaseDate: holding?.purchase_date ?? "",
      expenseRatio: holding?.expense_ratio ?? undefined,
      notes: holding?.notes ?? "",
    },
  });

  const onSubmit = async (values: IndexFundFormValues) => {
    await onSave(values);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        aria-label={holding ? "Edit index fund holding" : "Add index fund holding"}
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="fundSymbol"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fund Symbol *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. VFIAX"
                    className="uppercase"
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fundName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fund Name *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. Vanguard 500 Index Fund"
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="fundCategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fund Category *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger aria-required="true" aria-label="Select fund category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {INDEX_FUND_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="benchmarkIndex"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Benchmark Index *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. S&P 500"
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="unitsHeld"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Units Held *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.000001"
                    min="0"
                    placeholder="e.g. 10.5"
                    aria-required="true"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="averageCostPerUnit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Avg Cost per Unit *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="e.g. 350.00"
                    aria-required="true"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger aria-required="true" aria-label="Select currency">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {SUPPORTED_CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="purchaseDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Purchase Date *</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    aria-required="true"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="expenseRatio"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expense Ratio (%)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  max="5"
                  placeholder="e.g. 0.03"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === "" ? undefined : parseFloat(e.target.value)
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Optional notes about this index fund holding"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {holding ? "Save Changes" : "Add Index Fund"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
